import { action, computed, makeObservable, observable } from 'mobx'
import type { FirebaseRepository } from '../models/FirebaseRepository'
import {
  InstitutionIntegrationType,
  OrganizationState,
  type OrganizationInvoiceStatus,
  type StaticModelCollection,
} from '../types'
import { Cubit } from './core'
import { Organization } from '../models/Organization'
import { getInstitutions } from '../firestore/Institution'
import { Institution } from '../models/Institution'
import {
  createOrganization,
  getOrganizations,
  getOrganizationsWhereUserIsAdmin,
} from '../firestore/Organization'
import { getCatalogs } from '../firestore/Catalog'
import { Catalog } from '../models/Catalog'
import { getInstitutionIntegrations } from '../firestore/InstitutionIntegration'
import { InstitutionIntegration } from '../models/InstitutionIntegration'

export type OrgSortStrategy = 'institution' | 'organization' | 'platform'

export class OrganizationsCubit extends Cubit {
  repository: FirebaseRepository
  private _organizations: StaticModelCollection<Organization>
  catalogs: StaticModelCollection<Catalog>
  institutions: StaticModelCollection<Institution>
  integrations = observable.map<
    string,
    StaticModelCollection<InstitutionIntegration>
  >()

  @observable
  showArchived = false

  @observable
  searchTerm = ''

  @observable
  platformFilter = 'all'

  @observable
  sortingStrategy: OrgSortStrategy | 'default' = 'default'

  @observable
  sortingOrder: 'asc' | 'desc' = 'asc'

  constructor(repository: FirebaseRepository) {
    super()
    makeObservable(this)
    this.repository = repository
    this._organizations = Organization.emptyCollection(repository)
    this.institutions = Institution.emptyCollection(repository)
    this.catalogs = Catalog.emptyCollection(repository)
  }

  initialize(): void {
    this.setupOrganizationStreams()
    if (this.repository.breakoutUser?.isCorre) {
      this.addStream(getInstitutions(this.repository), (institutions) => {
        this.institutions.replaceModels(institutions)
        this.addIntegrationStreams(institutions)
      })
      this.addStream(getCatalogs(this.repository), (catalogs) => {
        this.catalogs.replaceModels(
          catalogs.sort((a, b) =>
            a.data.catalogName.localeCompare(b.data.catalogName)
          )
        )
      })
    } else {
      // If the user is not an admin, we don't need to fetch the institutions/catalogs
      // set empty to move loading state to false
      this.institutions.replaceModels([])
      this.catalogs.replaceModels([])
    }
  }

  setupOrganizationStreams() {
    if (this.repository.breakoutUser?.isInternal) {
      // When the user is internal, we can just do a list query on all the organizations.
      this.addStream(getOrganizations(this.repository), (orgs) => {
        this._organizations.replaceModels(orgs)
      })
      return
    }

    // When the user is not internal, we need to get the organizations that the current user is an admin of.
    this.addStream(
      getOrganizationsWhereUserIsAdmin(
        this.repository,
        this.repository.breakoutUser?.uid || ''
      ),
      (orgs) => {
        this._organizations.replaceModels(orgs)
      }
    )
  }

  addIntegrationStreams(institutions: Institution[]) {
    const namespace = 'integrations'
    this.removeStreams(namespace)

    for (const institution of institutions) {
      this.addStream(
        getInstitutionIntegrations(this.repository, {
          institutionId: institution.id,
        }),
        (integrations) => {
          const found = this.integrations.get(institution.id)
          if (found) {
            found.replaceModels(integrations)
          } else {
            const collection = InstitutionIntegration.emptyCollection(
              this.repository
            )
            collection.replaceModels(integrations)
            this.integrations.set(institution.id, collection)
          }
        },
        { namespace: namespace }
      )
    }
  }

  sortOrgsByInstNameAndOrgName = (orgs: Organization[]) => {
    // Sort by institution name first, then organization name.
    return orgs.sort((a, b) => {
      const aInstitution = a.data.organizationInstitution || ''
      const bInstitution = b.data.organizationInstitution || ''
      if (aInstitution === bInstitution) {
        return a.data.organizationName.localeCompare(b.data.organizationName)
      }
      return aInstitution.localeCompare(bInstitution)
    })
  }

  @action
  setSearchTerm(searchTerm: string) {
    this.searchTerm = searchTerm
  }

  @action
  toggleShowArchived() {
    this.showArchived = !this.showArchived
  }

  createOrganization(params: {
    organizationName: string
    organizationInstitution: string
    institutionId: string
    organizationInvoiceStatus: OrganizationInvoiceStatus
    catalogIds: string[]
  }): Promise<string> {
    return createOrganization(this.repository, {
      ...params,
    })
  }

  @computed
  get loading() {
    return (
      this._organizations.isLoading ||
      this.institutions.isLoading ||
      this.catalogs.isLoading
    )
  }

  @computed
  get organizations() {
    let filtered = this._organizations.models
    if (!this.showArchived) {
      // Only show active organizations
      filtered = filtered.filter(
        (org) => org.data.organizationState === OrganizationState.active
      )
    }

    if (this.searchTerm.trim() !== '') {
      const filterTerm = this.searchTerm.trim().toLowerCase()
      filtered = filtered.filter((org) => {
        const institutionName = org.data.organizationInstitution
        return (
          org.data.organizationName.toLowerCase().includes(filterTerm) ||
          institutionName?.toLowerCase().includes(filterTerm)
        )
      })
    }

    return this.sortOrgsByInstNameAndOrgName(filtered)
  }

  getOrgIntegrations(org: Organization) {
    const institution = this.institutions.models.find(
      (i) => i.id === org.data.institutionId
    )
    const instIntegrations = institution
      ? this.integrations.get(institution.id)
      : undefined
    return { institution, integrationModels: instIntegrations?.models ?? [] }
  }

  private getPlatformLabel(org: Organization): string {
    const { integrationModels } = this.getOrgIntegrations(org)
    if (integrationModels.length === 0) return 'direct'
    return [...new Set(integrationModels.map((i) => i.data.integrationType))]
      .sort()
      .join(', ')
  }

  @computed
  get platformCounts() {
    const counts = { all: 0, direct: 0, canvas: 0, brightspace: 0 }
    counts.all = this.organizations.length
    for (const org of this.organizations) {
      const { integrationModels } = this.getOrgIntegrations(org)
      if (integrationModels.length === 0) {
        counts.direct++
      } else {
        if (
          integrationModels.some(
            (i) => i.data.integrationType === InstitutionIntegrationType.canvas
          )
        ) {
          counts.canvas++
        }
        if (
          integrationModels.some(
            (i) =>
              i.data.integrationType === InstitutionIntegrationType.brightspace
          )
        ) {
          counts.brightspace++
        }
      }
    }
    return counts
  }

  @computed
  get filteredOrganizations() {
    if (this.platformFilter === 'all') return this.organizations

    return this.organizations.filter((org) => {
      const { integrationModels } = this.getOrgIntegrations(org)
      if (integrationModels.length === 0) {
        return this.platformFilter === 'direct'
      }
      return integrationModels.some(
        (integration) =>
          this.platformFilter === integration.data.integrationType
      )
    })
  }

  @computed
  get sortedOrganizations() {
    if (this.sortingStrategy === 'default') return this.filteredOrganizations

    return [...this.filteredOrganizations].sort((a, b) => {
      let comparison = 0
      switch (this.sortingStrategy) {
        case 'institution':
          comparison = this.repository.utils.compareStrings(
            a.data.organizationInstitution,
            b.data.organizationInstitution
          )
          break
        case 'organization':
          comparison = this.repository.utils.compareStrings(
            a.data.organizationName,
            b.data.organizationName
          )
          break
        case 'platform':
          comparison = this.repository.utils.compareStrings(
            this.getPlatformLabel(a),
            this.getPlatformLabel(b)
          )
          break
      }
      return this.sortingOrder === 'asc' ? comparison : -comparison
    })
  }

  @action
  changeSortingStrategy(
    strategy: OrgSortStrategy | 'default',
    order: 'asc' | 'desc'
  ) {
    this.sortingStrategy = strategy
    this.sortingOrder = order
  }

  @action
  setPlatformFilter(filter: string) {
    this.platformFilter = filter
  }
}
